import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Warms the persisted React Query cache with every core dataset while online,
 * so screens the doctor has never opened still render offline.
 * Runs once per session and again whenever connectivity returns.
 */
const TASKS: { key: unknown[]; run: () => Promise<unknown> }[] = [
  {
    key: ["appointments"],
    run: async () =>
      (await supabase.from("appointments").select("*, patients(name, phone), chambers(name)").order("date", { ascending: false }).limit(500)).data ?? [],
  },
  {
    key: ["invoices"],
    run: async () =>
      (await supabase.from("invoices").select("*, patients(name, phone)").order("date", { ascending: false }).limit(300)).data ?? [],
  },
  {
    key: ["lab-orders", "all"],
    run: async () =>
      (await supabase.from("lab_orders").select("*, patients(name, phone)").order("ordered_date", { ascending: false }).limit(300)).data ?? [],
  },
  {
    key: ["follow-ups", "all"],
    run: async () =>
      (await supabase.from("follow_up_reminders").select("*, patients(name, phone)").order("reminder_date", { ascending: true }).limit(300)).data ?? [],
  },
  {
    key: ["referrals", "all"],
    run: async () =>
      (await supabase.from("referrals").select("*, patients(name, phone)").order("referral_date", { ascending: false }).limit(200)).data ?? [],
  },
  {
    key: ["vaccinations", "all"],
    run: async () =>
      (await supabase.from("vaccinations").select("*, patients(name, phone, age)").order("date_given", { ascending: false }).limit(300)).data ?? [],
  },
  { key: ["medicines"], run: async () => (await supabase.from("medicines").select("*").order("name").limit(1000)).data ?? [] },
  { key: ["inventory"], run: async () => (await supabase.from("inventory").select("*").order("medicine_name").limit(500)).data ?? [] },
  { key: ["expenses"], run: async () => (await supabase.from("expenses").select("*").order("date", { ascending: false }).limit(300)).data ?? [] },
  { key: ["staff"], run: async () => (await supabase.from("staff").select("*").order("name").limit(200)).data ?? [] },
  { key: ["chambers"], run: async () => (await supabase.from("chambers").select("*").limit(100)).data ?? [] },
  { key: ["prescription-templates"], run: async () => (await supabase.from("prescription_templates").select("*").limit(300)).data ?? [] },
];

async function prewarm(userId: string, setData: (key: unknown[], data: unknown) => void) {
  for (const task of TASKS) {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    try {
      const data = await task.run();
      setData([...task.key, userId], data);
      setData(task.key, data);
    } catch {
      /* one failed table shouldn't stop the rest */
    }
  }
}

export function useOfflinePrewarm() {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const setData = (key: unknown[], data: unknown) => {
      if (!cancelled) qc.setQueryData(key, data);
    };

    // Delay so first paint and the active screen's own queries go first.
    const timer = window.setTimeout(() => void prewarm(user.id, setData), 4000);
    const onOnline = () => void prewarm(user.id, setData);
    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("online", onOnline);
    };
  }, [user, qc]);
}

export function OfflinePrewarm() {
  useOfflinePrewarm();
  return null;
}
